/**
 * Tab "Ghi chú GV" — textarea bound to attendance_session.teacher_note.
 * Explicit save button. Unsaved-changes warning via beforeunload.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateTeacherNote } from '@client/lib/queries/use-teacher-note';

interface TabTeacherNoteProps {
  classId: string;
  sessionId: string;
  /** ID of the AttendanceSession record (may differ from lessonSession id) */
  attendanceSessionId: string;
  initialNote?: string | null;
}

export function TabTeacherNote({
  classId,
  sessionId,
  attendanceSessionId,
  initialNote,
}: TabTeacherNoteProps) {
  const [note, setNote] = useState(initialNote ?? '');
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const isDirty = note !== (initialNote ?? '');

  // Sync if parent data changes (e.g. dashboard refetch)
  useEffect(() => {
    setNote(initialNote ?? '');
    setSavedAt(null);
  }, [initialNote, attendanceSessionId]);

  // Unsaved-changes warning
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = 'Có thay đổi chưa lưu. Bạn có chắc muốn rời trang?';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const updateNote = useUpdateTeacherNote(classId, sessionId);

  async function handleSave() {
    try {
      await updateNote.mutateAsync({
        attendanceSessionId,
        teacherNote: note.trim() || null,
      });
      setSavedAt(new Date());
      toast.success('Đã lưu ghi chú.');
    } catch {
      toast.error('Lỗi khi lưu ghi chú.');
    }
  }

  return (
    <div className="space-y-3 pb-24 md:pb-4">
      {savedAt && !isDirty && (
        <p className="text-xs text-muted-foreground">
          Đã lưu lúc{' '}
          {savedAt.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}
      {isDirty && (
        <p className="text-xs text-muted-foreground">Có thay đổi chưa lưu.</p>
      )}

      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Ghi chú sau buổi học (hỗ trợ Markdown)..."
        className="min-h-[240px] resize-y text-sm"
        maxLength={5000}
      />

      <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t border-border p-3 flex gap-2 md:static md:border-0 md:p-0 md:bg-transparent">
        <Button
          className="flex-1 min-h-[44px]"
          onClick={() => void handleSave()}
          disabled={updateNote.isPending || !isDirty}
        >
          <Save className="mr-2 h-4 w-4" />
          {updateNote.isPending ? 'Đang lưu...' : 'Lưu ghi chú'}
        </Button>
      </div>
    </div>
  );
}
